import { lazy, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { LoginPage } from "@/auth/LoginPage";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import {
  AIS_VESSEL_TRACKING,
  INVOICE_RECONCILIATION,
  useFeatureEnabled,
  FeatureFlagsProvider,
} from "@/context/FeatureFlagsContext";
import { SurchargeCodesProvider } from "@/context/SurchargeCodesContext";
import { ToastProvider } from "@/context/ToastContext";
import { useUser } from "@/auth/UserContext";
import { AdminRoute } from "@/auth/AdminRoute";
import { RateCreateRoute } from "@/auth/RateCreateRoute";
import { KaiChatPage } from "@/features/kai/KaiChatPage";
import { AppShellLayout } from "@/shell/AppShellLayout";
import { AppBootstrapGate } from "./AppBootstrapGate";
import { routeModules } from "./routeModules";
// Kai (`/`) is the default landing page and stays eager. Every other page is a
// separate chunk so the initial bundle (and the tracking Three.js payload in
// particular) isn't downloaded until the route is visited or prefetched.
const HomePage = lazy(() =>
  routeModules.dashboard().then((m) => ({ default: m.HomePage })),
);
const BookingsPage = lazy(() =>
  routeModules.bookings().then((m) => ({ default: m.BookingsPage })),
);
const BookingDetailPage = lazy(() =>
  routeModules.bookingDetail().then((m) => ({ default: m.BookingDetailPage })),
);
const RatesPage = lazy(() =>
  routeModules.rates().then((m) => ({ default: m.RatesPage })),
);
const RateCreatePage = lazy(() =>
  routeModules.rateCreate().then((m) => ({ default: m.RateCreatePage })),
);
const RateCreateFromDocumentPage = lazy(routeModules.rateCreateFromDocument);
const RateDetailPage = lazy(() =>
  routeModules.rateDetail().then((m) => ({ default: m.RateDetailPage })),
);
const SchedulePage = lazy(() =>
  routeModules.schedule().then((m) => ({ default: m.SchedulePage })),
);
const TrackingPage = lazy(() =>
  routeModules.tracking().then((m) => ({ default: m.TrackingPage })),
);
const ReportsPage = lazy(() =>
  routeModules.reports().then((m) => ({ default: m.ReportsPage })),
);
const ReportDetailPage = lazy(() =>
  routeModules.reportDetail().then((m) => ({ default: m.ReportDetailPage })),
);
const InvoicesPage = lazy(() =>
  routeModules.invoices().then((m) => ({ default: m.InvoicesPage })),
);
const InvoiceBatchPage = lazy(() =>
  routeModules.invoiceBatch().then((m) => ({ default: m.InvoiceBatchPage })),
);
const InvoiceUploadLinePage = lazy(() =>
  routeModules.invoiceUploadLine().then((m) => ({
    default: m.InvoiceUploadLinePage,
  })),
);
const FeedPage = lazy(() =>
  routeModules.feed().then((m) => ({ default: m.FeedPage })),
);
const PreferencesPage = lazy(() =>
  routeModules.preferences().then((m) => ({ default: m.PreferencesPage })),
);
const AdminPage = lazy(() =>
  routeModules.admin().then((m) => ({ default: m.AdminPage })),
);

function FeatureGate({
  flag,
  children,
}: {
  flag: string;
  children: ReactNode;
}) {
  const enabled = useFeatureEnabled(flag);
  if (!enabled) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Gate the Invoices page: feature flag on, caller holds `read:invoices`, and is
 * not a SalesPerson (sales users never see invoices).
 */
function InvoicesRoute({ children }: { children: ReactNode }) {
  const enabled = useFeatureEnabled(INVOICE_RECONCILIATION);
  const { isSalesPerson, isClient, permissions } = useUser();
  if (
    !enabled ||
    isSalesPerson ||
    isClient ||
    !permissions.includes("read:invoices")
  ) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Block Client (consignee portal) users from rates / opportunity surfaces.
 * Their bookings are hard-scoped server-side; these pages expose carrier
 * pricing and savings they must never see.
 */
function ClientBlockedRoute({ children }: { children: ReactNode }) {
  const { isClient } = useUser();
  if (isClient) {
    return <Navigate to="/bookings" replace />;
  }
  return <>{children}</>;
}

/** Lazy route elements suspend on the shell {@link ShellRouteOutlet} boundary. */
function Lazy({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/** Auth + bootstrap providers; child layout routes render through `<Outlet />`. */
function AuthenticatedShell() {
  return (
    <ProtectedRoute>
      <FeatureFlagsProvider>
        <SurchargeCodesProvider>
          <ToastProvider>
            <AppBootstrapGate>
              <Outlet />
            </AppBootstrapGate>
          </ToastProvider>
        </SurchargeCodesProvider>
      </FeatureFlagsProvider>
    </ProtectedRoute>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<AuthenticatedShell />}>
        <Route element={<AppShellLayout />}>
        <Route path="/" element={<KaiChatPage />} />
        <Route
          path="/dashboard"
          element={
            <Lazy>
              <HomePage />
            </Lazy>
          }
        />
        <Route
          path="/bookings"
          element={
            <Lazy>
              <BookingsPage />
            </Lazy>
          }
        />
        <Route
          path="/bookings/:sourceId"
          element={
            <Lazy>
              <BookingDetailPage />
            </Lazy>
          }
        />
        <Route
          path="/rates"
          element={
            <ClientBlockedRoute>
              <Lazy>
                <RatesPage />
              </Lazy>
            </ClientBlockedRoute>
          }
        />
        <Route
          path="/rates/create"
          element={
            <RateCreateRoute>
              <Lazy>
                <RateCreatePage />
              </Lazy>
            </RateCreateRoute>
          }
        />
        <Route
          path="/rates/create/from-document"
          element={
            <RateCreateRoute>
              <Lazy>
                <RateCreateFromDocumentPage />
              </Lazy>
            </RateCreateRoute>
          }
        />
        <Route
          path="/rates/:cardId"
          element={
            <ClientBlockedRoute>
              <Lazy>
                <RateDetailPage />
              </Lazy>
            </ClientBlockedRoute>
          }
        />
        <Route
          path="/schedule"
          element={
            <Lazy>
              <SchedulePage />
            </Lazy>
          }
        />
        <Route
          path="/invoices"
          element={
            <InvoicesRoute>
              <Lazy>
                <InvoicesPage />
              </Lazy>
            </InvoicesRoute>
          }
        />
        <Route
          path="/invoices/batches/:batchId"
          element={
            <InvoicesRoute>
              <Lazy>
                <InvoiceBatchPage />
              </Lazy>
            </InvoicesRoute>
          }
        />
        <Route
          path="/invoices/batches/:batchId/lines/:lineId"
          element={
            <InvoicesRoute>
              <Lazy>
                <InvoiceUploadLinePage />
              </Lazy>
            </InvoicesRoute>
          }
        />
        <Route
          path="/tracking"
          element={
            <FeatureGate flag={AIS_VESSEL_TRACKING}>
              <Lazy>
                <TrackingPage />
              </Lazy>
            </FeatureGate>
          }
        />
        <Route
          path="/reports"
          element={
            <ClientBlockedRoute>
              <Lazy>
                <ReportsPage />
              </Lazy>
            </ClientBlockedRoute>
          }
        />
        <Route
          path="/reports/:reportId"
          element={
            <ClientBlockedRoute>
              <Lazy>
                <ReportDetailPage />
              </Lazy>
            </ClientBlockedRoute>
          }
        />
        <Route
          path="/feed"
          element={
            <Lazy>
              <FeedPage />
            </Lazy>
          }
        />
        <Route
          path="/preferences"
          element={
            <Lazy>
              <PreferencesPage />
            </Lazy>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Lazy>
                <AdminPage />
              </Lazy>
            </AdminRoute>
          }
        />
        <Route
          path="/preview"
          element={
            <Lazy>
              <HomePage />
            </Lazy>
          }
        />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
